// Contenido del diálogo «Modelo»: los parámetros del SCI en solo lectura. La edición vive
// en Configuración global. Cada juego de pesos muestra su suma para que un set descalibrado se vea.
import { AlertTriangle, Check } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { NO_VALUE } from '../logic';
import type { ScoringParameterGroup } from '../types';

const CARD = 'overflow-hidden rounded-md border border-border bg-card';
const CARD_HEAD =
    'flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2';
const CELL = 'border-b border-border/50 px-3 py-1.5 align-top text-[11.5px]';
const HEAD =
    'border-b border-border bg-muted/60 px-3 py-1.5 text-left text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

function SumBadge({ group }: { group: ScoringParameterGroup }) {
    if (group.sum === null || group.expectedSum === null) return null;
    const matches = group.sumMatchesExpected === true;
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold',
                matches ? 'bg-success/15 text-success' : 'bg-destructive/15 text-destructive',
            )}
        >
            {matches ? (
                <Check className="size-3" aria-hidden />
            ) : (
                <AlertTriangle className="size-3" aria-hidden />
            )}
            {`Suma ${String(group.sum)} de ${String(group.expectedSum)}`}
        </span>
    );
}

function GroupCard({ group }: { group: ScoringParameterGroup }) {
    return (
        <section className={cn(CARD, 'mb-3.5')}>
            <header className={CARD_HEAD}>
                <div className="min-w-0">
                    <h3 className="text-[12px] font-semibold">{group.title}</h3>
                    <p className="text-[10.5px] text-muted-foreground">{group.hint}</p>
                </div>
                <SumBadge group={group} />
            </header>

            {group.items.length === 0 ? (
                <p className="px-3 py-3 text-[11.5px] text-muted-foreground">
                    El motor no devolvió ninguno de los parámetros de este grupo.
                </p>
            ) : (
                <table className="w-full table-fixed border-collapse">
                    <caption className="sr-only">{group.title}</caption>
                    <thead>
                        <tr>
                            <th scope="col" className={cn(HEAD, 'w-[26%]')}>
                                Clave
                            </th>
                            <th scope="col" className={cn(HEAD, 'w-[13%] text-right')}>
                                Vigente
                            </th>
                            <th scope="col" className={cn(HEAD, 'w-[13%] text-right')}>
                                Por defecto
                            </th>
                            <th scope="col" className={cn(HEAD, 'w-[48%]')}>
                                Qué controla
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {group.items.map((item) => (
                            <tr key={item.key}>
                                <td className={cn(CELL, 'font-mono text-[11px]')}>{item.key}</td>
                                <td
                                    className={cn(
                                        CELL,
                                        'text-right font-bold tabular-nums',
                                        item.differsFromDefault && 'text-warning',
                                    )}
                                >
                                    {item.value || NO_VALUE}
                                    {item.differsFromDefault ? (
                                        <span className="ml-1 text-[9.5px] font-normal uppercase text-warning">
                                            cambiado
                                        </span>
                                    ) : null}
                                </td>
                                <td
                                    className={cn(
                                        CELL,
                                        'text-right tabular-nums text-muted-foreground',
                                    )}
                                >
                                    {item.defaultValue}
                                </td>
                                <td className={cn(CELL, 'text-muted-foreground')}>
                                    {item.description || NO_VALUE}
                                    <div className="mt-0.5 text-[10px]">
                                        {item.updatedAtIso === null
                                            ? 'No se sabe cuándo se editó'
                                            : `Última edición ${formatDateMx(item.updatedAtIso)}`}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {group.missingKeys.length > 0 ? (
                <p className="border-t border-border bg-warning/10 px-3 py-2 text-[11px]">
                    {`El motor no devolvió: ${group.missingKeys.join(', ')}.`}
                </p>
            ) : null}
        </section>
    );
}

export interface ModeliaModelTabProps {
    groups: readonly ScoringParameterGroup[];
}

export function ModeliaModelTab({ groups }: ModeliaModelTabProps) {
    return (
        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/15 px-5 py-4">
            <p className="mb-3.5 rounded-md border border-border bg-card px-3 py-2 text-[11.5px] text-muted-foreground">
                Los parámetros con los que el motor calcula el SCI, en{' '}
                <strong className="font-semibold text-foreground">solo lectura</strong>. Se editan
                en Configuración global, no aquí.
            </p>
            {groups.map((group) => (
                <GroupCard key={group.key} group={group} />
            ))}
        </div>
    );
}
